import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID, Service, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { debounceTime, fromEvent } from 'rxjs';

@Service()
export class DeviceService {
  private readonly _mobileWidth: number = 768;

  private readonly _desktopWidth: number = 1536;

  private readonly _resizeEvent = 'resize';

  private readonly _debounceTime = 10;

  private readonly _isBrowserPlatform: boolean;

  private _window: Window | null = null;

  public isMobile = signal(false);

  public isTablet = signal(false);

  public isDesktop = signal(false);

  private get screenWidth(): number {
    return this._window?.innerWidth ?? 0;
  }

  constructor() {
    const platformId = inject(PLATFORM_ID);
    this._isBrowserPlatform = isPlatformBrowser(platformId);

    if (this._isBrowserPlatform && typeof document !== 'undefined') {
      this._window = document.defaultView;
      this.initializeResizeSignal();
    }

    this.updateResolution();
  }

  public isBrowser(): boolean {
    return this._isBrowserPlatform;
  }

  public isServer(): boolean {
    return !this._isBrowserPlatform;
  }

  private isMobileResolution(): boolean {
    return this.screenWidth < this._mobileWidth;
  }

  private isTabletResolution(): boolean {
    return this.screenWidth >= this._mobileWidth && this.screenWidth < this._desktopWidth;
  }

  private isDesktopResolution(): boolean {
    return this.screenWidth >= this._desktopWidth;
  }

  private updateResolution(): void {
    if (!this._isBrowserPlatform) {
      return;
    }

    this.isMobile.set(this.isMobileResolution());
    this.isTablet.set(this.isTabletResolution());
    this.isDesktop.set(this.isDesktopResolution());
  }

  private initializeResizeSignal(): void {
    if (!this._window) {
      return;
    }

    const resizeEvent = toSignal(
      fromEvent(this._window, this._resizeEvent).pipe(debounceTime(this._debounceTime)),
      { initialValue: null }
    );
    effect(() => {
      resizeEvent();
      this.updateResolution();
    });
  }
}
